/**
 * The listings filter SET — the one app-side filter currency.
 *
 * The SSR loader decodes the URL through the shared codec
 * (`@letsgethome/routes` `decodeListingsFilterParams`) exactly once and hands
 * the result to BOTH projections:
 *
 *   URL ──decode──▶ ListingsFilterSet ──┬─▶ toProtoFilters()  (SSR RPC, mappers/storefront/listings-filters.ts)
 *                                       └─▶ toBffFilters()    (client refetch, this file)
 *
 * Before this seam existed the client half simply never received the set (the
 * pages built `<ListingsClient>` without the prop), so panning the map after a
 * filtered load re-queried by BOUNDS ONLY — silently dropping the user's
 * district/estate/price/bedroom selection — and the filter badge counted zero
 * forever. The set is therefore a REQUIRED prop, not an optional one: a page
 * that forgets it no longer compiles.
 *
 * Values stay string-level (the codec's decoded shape) all the way to the
 * client because this crosses the SSR loader-data boundary, which is JSON —
 * a `bigint` cents field here would throw on serialization. The typed
 * conversion happens at each projection's own seam.
 */
import type { MapBounds } from '@/shared/lib/geo/bounds';
import { asPropertyTypeName } from '@/shared/lib/types/listing';
import type { SearchListingsParams } from '@/shared/lib/types/search';
import { activeFilterCount, priceFilterBound, roomFilterMin } from '@letsgethome/lib/wasm';
import type { DecodedListingsFilterParams } from '@letsgethome/routes';
import { centsBigInt } from '@letsgethome/types/branded';
import type { DistrictSlug } from '@letsgethome/types/districts';

/**
 * The decoded listings filter set, plus the district FACET scope.
 *
 * `districtSlug` is not a URL filter param — it comes from the
 * `/districts/:slug/{rent,buy}` path segment — but it is a discrete location
 * on the wire (`SearchFilters.district_slug`), so it belongs to the same set:
 * it participates in the bounds XOR below and must survive a client refetch
 * exactly like `districtId` does.
 */
export interface ListingsFilterSet extends DecodedListingsFilterParams {
  districtSlug?: DistrictSlug;
}

/** An integer param, or undefined when absent/malformed. */
function int(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * A bedrooms/bathrooms MINIMUM, or undefined. The "0 = any" unset decision is
 * the Rust filters SSOT's (`roomFilterMin`) — never re-derived here.
 */
function rooms(raw: string | undefined): number | undefined {
  const n = int(raw);
  return n === undefined ? undefined : roomFilterMin(n);
}

/**
 * A `*_price` cents param → branded cents, or undefined. The set/unset
 * decision (0 = any) is the Rust filters SSOT's (`priceFilterBound`).
 */
function cents(raw: string | undefined): ReturnType<typeof centsBigInt> | undefined {
  if (raw === undefined) return undefined;
  let value: bigint;
  try {
    value = BigInt(raw);
  } catch {
    return undefined;
  }
  return priceFilterBound(Number(value)) !== undefined ? centsBigInt(value) : undefined;
}

/**
 * Whether the set names a DISCRETE location (a district, a district facet, or
 * an estate).
 *
 * The wire forbids combining one with a map viewport — CEL
 * `search.filters.bounds_xor_discrete_location`. Both projections consult this
 * one predicate so the SSR request and the client refetch cannot disagree
 * about which of the two wins (they must, or a map pan on a district-scoped
 * page would build a request the backend rejects outright).
 */
export function hasDiscreteLocation(set: ListingsFilterSet): boolean {
  return Boolean(set.districtId ?? set.districtSlug ?? set.estateId);
}

/**
 * Resolve the map viewport against the set under the wire's XOR rule: a
 * discrete location always wins, so the viewport is honoured only when the
 * user has not pinned a district/estate.
 */
export function resolveViewport(
  set: ListingsFilterSet,
  viewport: MapBounds | undefined
): MapBounds | undefined {
  return hasDiscreteLocation(set) ? undefined : viewport;
}

/**
 * Project the set onto the `/api/search/listings` BFF filter shape, folding in
 * the current map viewport under the XOR rule. Returns `undefined` when
 * nothing is filtered, so a bare `/listings/rent` sends no filters at all.
 */
export function toBffFilters(
  set: ListingsFilterSet,
  viewport?: MapBounds
): SearchListingsParams['filters'] {
  const bounds = resolveViewport(set, viewport);
  const filters: NonNullable<SearchListingsParams['filters']> = {};

  if (bounds) filters.bounds = bounds;
  if (set.districtId) filters.districtId = set.districtId;
  if (set.districtSlug) filters.districtSlug = set.districtSlug;
  if (set.estateId) filters.estateId = set.estateId;

  const minPriceCents = cents(set.minPrice);
  const maxPriceCents = cents(set.maxPrice);
  if (minPriceCents !== undefined) filters.minPriceCents = minPriceCents;
  if (maxPriceCents !== undefined) filters.maxPriceCents = maxPriceCents;

  const bedrooms = rooms(set.bedrooms);
  const bathrooms = rooms(set.bathrooms);
  if (bedrooms !== undefined) filters.bedrooms = bedrooms;
  if (bathrooms !== undefined) filters.bathrooms = bathrooms;

  const query = set.query?.trim() || undefined;
  if (query) filters.query = query;

  // Only the literal `true` sets it — the wire's `false` IS "no pets filter".
  if (set.petsAllowed === 'true') filters.petsAllowed = true;
  const propertyType = asPropertyTypeName(set.propertyType);
  if (propertyType) filters.propertyType = propertyType;

  return Object.keys(filters).length > 0 ? filters : undefined;
}

/**
 * The count of REFINEMENT filters only (price range, rooms, pets) — what the
 * filters modal's reset affordance keys off.
 */
export function refinementFilterCount(refinements: {
  minPriceCents: bigint | null;
  maxPriceCents: bigint | null;
  bedrooms: number | null;
  bathrooms: number | null;
  petsAllowed: boolean;
}): number {
  return activeFilterCount(
    refinements.minPriceCents !== null,
    refinements.maxPriceCents !== null,
    refinements.bedrooms ?? 0,
    refinements.bathrooms ?? 0,
    refinements.petsAllowed,
    // The browsing CONTEXT (district/estate/query) is deliberately excluded:
    // this count drives the filters modal's "reset" affordance, and reset
    // clears refinements only — the partition is the Rust SSOT's
    // (`filter_field_clears_on_clear_all`), not this call site's.
    false,
    false,
    false
  );
}

/**
 * The number the filter badge shows — the Rust filters SSOT's count
 * (`domain_core::filters::active_filter_count`), reached through the one
 * named-argument adapter so no call site writes the wasm positional form.
 *
 * The badge previously hand-rolled this and omitted the district, estate and
 * query dimensions — the same drift the SSOT was created to settle when
 * Android and iOS disagreed.
 */
export function filterBadgeCount(set: ListingsFilterSet): number {
  return activeFilterCount(
    cents(set.minPrice) !== undefined,
    cents(set.maxPrice) !== undefined,
    rooms(set.bedrooms) ?? 0,
    rooms(set.bathrooms) ?? 0,
    set.petsAllowed === 'true',
    Boolean(set.districtId ?? set.districtSlug),
    Boolean(set.estateId),
    Boolean(set.query?.trim())
  );
}
